import React, { useRef, useEffect, useState } from 'react';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import {
  MIN_SEARCH_LENGTH,
  DELAY_SEARCH_IN_MS,
} from 'Search/action-creators';
import { SearchGroup } from '../SearchPage';
import { getResposiveFontSize, InlineLabel, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { GridWrap } from 'Shared/PageLayout';
import * as style from 'Shared/Style';

export type PropType = {
  totalResultCount: number;
  searchGroupsListFiltered: SearchGroup[];
  selectedGroup: SearchGroup;
  searchPhrase: string;
  labels: {[key: string]: string};
  selectGroup: (group: SearchGroup) => void;
  executeSearch: (query: string) => void;
  setSearchPhrase: (query: string) => void;
  isMobile: boolean;
};

const SearchInput = styled.input({
  border: {
    xy: {
      width: '0',
      style: 'none',
    },
  },
  margin: {
    xy: '0px',
  },
  padding: {
    xy: '0px',
  },  
  lineHeight: '100%',
  textTransform: 'uppercase',
  background: 'none',
  color: 'black',
  fontWeight: 500,
});

const SearchContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '8px',
});

const SearchButton = styled.button({
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '100%',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  color: style.colors.monochrome.a11ygrey,
  cursor: 'pointer',
  padding: {
    xy: 0
  },
  background: 'none',
  border: {
    xy: {
      width: '0',
      style: 'none',
    },
  },
});

const InlineLabelInactive = styled(InlineLabel, {
  color: style.colors.monochrome.a11ygrey,
})

const InlineLabelActive = styled(InlineLabel, {
  paddingBottom: '4px',
  border: {
    bottom: {
      width: '2px',
      style: 'solid',
      color: style.colors.monochrome.black
    },
  },
})

const NoResultText = styled.p({
  color: style.colors.negativeColors.negative,
  textTransform: 'uppercase',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '100%',
  padding: {
    xy: 0
  },
  margin: {
    xy: 0
  },
})

const Container = styled.div({
  display: 'flex',
  gap: '16px',
});

const Wrap = styled(GridWrap, {
  paddingTop: '48px',
  paddingBottom: '48px',
  ['@media ' + style.mediaMinLarge]: {
    paddingBottom: '80px'
  },
})

const FlexWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
})

const HiddenField = styled.span({
  lineHeight: '100%',
  textTransform: 'uppercase',
  width: 'auto',
  fontWeight: 500,
})

const SearchPageTop = connect(
  (state) => ({
    currentBreakpoint: state.currentBreakpoint,
  }),
)(
  (props: PropType) => {

    const searchSpanRef = useRef<HTMLSpanElement>(null);
    let searchInputRef = useRef<HTMLInputElement>(null);
    
    const { totalResultCount, searchGroupsListFiltered, selectGroup, selectedGroup, isMobile, labels = {} } = props;
    
    //const [searchPhrase, setSearchPhrase] = useState<string>(props.searchPhrase);
    const [searchInputVisible, setSearchInputVisible] = useState<boolean>(false);
    const [timeoutNumber, setTimeoutNumber] = useState(undefined);
    
    const handleSearchInputChange = (elm: HTMLInputElement) => {
      searchSpanRef.current.innerText = searchInputRef.current.value;
      searchInputRef.current.style.width = `${searchSpanRef.current.offsetWidth}px`;
      
      if (timeoutNumber !== undefined) {
        clearTimeout(timeoutNumber);
      }
      const newSearchPhrase = elm.value
      const filterOnInputValue = () => {
        if (newSearchPhrase.length < MIN_SEARCH_LENGTH) {
          // Not enough letters, exit
          return;
        }
        props.executeSearch(newSearchPhrase);
      };

      props.setSearchPhrase(newSearchPhrase);
      setTimeoutNumber(setTimeout(filterOnInputValue, DELAY_SEARCH_IN_MS));
    }
    
    const handleShowSearchInput = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      searchInputRef.current.style.width = `${searchSpanRef.current.offsetWidth}px`;
      setSearchInputVisible(true);
      searchInputRef.current.focus(); 
    }
  
    const leaveSearchInput = (e: HTMLInputElement, e2: React.KeyboardEvent<HTMLElement>) => {
      if(e2) {
        if ((e2.target as HTMLElement).tagName.toLowerCase() === 'input' && (e2.keyCode || e2.which || e2.charCode || e2.key || 0) === 13) {
          handleSearchInputChange(e2.target as HTMLInputElement);
          setSearchInputVisible(false);
        }
        return;
      }
      
      setSearchInputVisible(false);
    }

    useEffect(() => {
      searchSpanRef.current.innerText = searchInputRef.current.value;
      searchInputRef.current.style.width = `${searchSpanRef.current.offsetWidth}px`;
    }, []);

    useEffect(() => {
      searchSpanRef.current.innerText = searchInputRef.current.value;
      searchInputRef.current.style.width = `${searchSpanRef.current.offsetWidth}px`;
    }, [props.searchPhrase]);

    useEffect(() => {
      if(searchInputVisible) {
        searchInputRef.current.focus();
      }
    }, [searchInputVisible]);

    return (
      <Wrap className="grid">
        <div className={createGridClasses(12, 10, createOffsetGridClassLarge(2))}>
          <FlexWrap>
            <Container>
              {totalResultCount > 0 && 
                <>
                  <div key={'sök'}>
                    <InlineLabel>{labels.searchResult}:</InlineLabel>
                  </div>
                  {searchGroupsListFiltered.map((searchGroup, i: number) => {
                    const SearchGroupLabelType = searchGroup.type === selectedGroup?.type ? InlineLabelActive : InlineLabelInactive;
                    return (
                      <styled.Div css={{ cursor: 'pointer' }} key={searchGroup.type} onClick={() => selectGroup(searchGroup)}>
                        <SearchGroupLabelType>{searchGroup.translation}</SearchGroupLabelType>
                      </styled.Div>
                    );
                  })}
                </>
              }
              {totalResultCount === 0 &&
                <NoResultText>
                  {labels.noResultsFor} "{props.searchPhrase}"
                  {labels.noResultsDescription && 
                    <>
                      <br />
                      {labels.noResultsDescription}
                    </>
                  }
                </NoResultText>
              }
            </Container>
            <SearchContainer>
              <HiddenField ref={searchSpanRef}
                className='visually-hidden'
                css={{
                  fontSize: getResposiveFontSize(ResponsiveSize.D96_M40, isMobile),
                }}
              >{props.searchPhrase}</HiddenField>
              <SearchInput
                ref={searchInputRef}
                type="text"
                value={props.searchPhrase}
                onChange={(e) => handleSearchInputChange(e.target)}
                onBlur={(e) => leaveSearchInput(e.target, null)}
                onKeyPress={(e) => leaveSearchInput(null, e)}
                disabled={!searchInputVisible}
                css={{
                  fontSize: getResposiveFontSize(ResponsiveSize.D96_M40, isMobile),
                }}
              />
              {!searchInputVisible && <SearchButton onClick={(e) => handleShowSearchInput(e)}>{labels.modifySearch}</SearchButton>}
            </SearchContainer>
          </FlexWrap>
        </div>
      </Wrap>
    );
})

export default SearchPageTop;